/**
 * Game Generation UI
 * Test interface for Game Generation Service
 */

import { BaseComponent } from './components/base-component';
import type { APIClient } from './api';
import type { ApiResponse } from './types';

// Interfaces for Game Generation Request


// Interface for Game Generation Response (from polling or initial trigger)
interface GameGenerationStatus {
    gameId: string;
    userId: string;
    status: 'generating' | 'completed' | 'failed';
    targetLanguage: string;
    proficiencyLevel: string;
    learningSubject: string;
    gameStyle: string;
    sceneSetting: string;
    r2PresignedUrl?: string; // Content URL
    errorMessage?: string;
    errorCode?: string;
    progress?: number;
}

export class GameGenerationUI extends BaseComponent {
    private apiClient: APIClient;
    private isGenerating: boolean = false;
    private pollInterval: NodeJS.Timeout | null = null;
    private gameId: string | null = null;
    private logs: string[] = [];

    constructor(container: HTMLElement, apiClient: APIClient) {
        super(container);
        this.apiClient = apiClient;
    }

    async render(): Promise<void> {
        this.container.innerHTML = `
      <div class="game-generation-ui">
        <div class="header-section">
          <h2>🎮 Game Generation Test</h2>
          <p class="subtitle">End-to-End test for AI Game Generation Service</p>
        </div>

        <div class="two-column-layout">
          <!-- Left Column: Controls -->
          <div class="card controls-card">
            <h3>Generation Parameters</h3>
            <form id="generation-form">
              <div class="form-group">
                <label>Target Language</label>
                <select id="gen-language" class="form-control">
                  <option value="japanese">Japanese</option>
                  <option value="english">English</option>
                  <option value="chinese">Chinese</option>
                  <option value="korean">Korean</option>
                  <option value="spanish">Spanish</option>
                  <option value="french">French</option>
                  <option value="german">German</option>
                  <option value="italian">Italian</option>
                  <option value="portuguese">Portuguese</option>
                  <option value="russian">Russian</option>
                  <option value="arabic">Arabic</option>
                  <option value="hindi">Hindi</option>
                </select>
              </div>
 
              <div class="form-group">
                <label>Dify API Key (Optional)</label>
                <input type="password" id="gen-apikey" class="form-control" placeholder="app-..." autocomplete="off" />
                <small class="form-text text-muted">Leave empty to use default workflow</small>
              </div>
 
              <div class="form-group">
                <label>Proficiency</label>
                <select id="gen-proficiency" class="form-control">
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
 
              <div class="form-group">
                <label>Topic / Subject</label>
                <select id="gen-topic" class="form-control">
                  <option value="greetings">Greetings</option>
                  <option value="travel">Travel</option>
                  <option value="food" selected>Food & Dining</option>
                  <option value="business">Business</option>
                  <option value="daily-life">Daily Life</option>
                  <option value="shopping">Shopping</option>
                  <option value="health">Health</option>
                  <option value="education">Education</option>
                  <option value="entertainment">Entertainment</option>
                  <option value="technology">Technology</option>
                  <option value="nature">Nature</option>
                  <option value="culture">Culture</option>
                </select>
              </div>
 
              <div class="form-group">
                <label>Game Style</label>
                <select id="gen-style" class="form-control">
                  <option value="fantasy-rpg">Fantasy RPG</option>
                  <option value="modern-life" selected>Modern Life</option>
                  <option value="picture-book">Picture Book</option>
                  <option value="historical">Historical</option>
                  <option value="sci-fi">Sci-Fi</option>
                </select>
              </div>
 
              <div class="form-group">
                <label>Scene Setting</label>
                <select id="gen-setting" class="form-control">
                  <option value="cafe">Cafe</option>
                  <option value="school">School</option>
                  <option value="office">Office</option>
                  <option value="park">Park</option>
                  <option value="home">Home</option>
                  <option value="fantasy-world">Fantasy World</option>
                  <option value="city">City</option>
                  <option value="village">Village</option>
                  <option value="dungeon">Dungeon</option>
                  <option value="space-station">Space Station</option>
                </select>
              </div>
 
              <div class="form-actions">
                <button type="submit" id="btn-generate" class="btn btn-primary">
                  <span>✨</span> Generate Game
                </button>
                <button type="button" id="btn-stop" class="btn btn-danger" disabled>
                  <span>⏹️</span> Stop
                </button>
              </div>
            </form>
          </div>

          <!-- Right Column: Status & Preview -->
          <div class="card results-card">
            <div class="card-header-row">
              <h3>Generation Status</h3>
              <span id="status-badge" class="badge badge-neutral">Idle</span>
            </div>
            
            <div id="status-log" class="status-log-container">
              <div class="empty-state">Ready to generate</div>
            </div>

            <div class="result-viewer mt-4">
              <h3>Game JSON Output</h3>
              <pre id="json-output" class="json-viewer">No data generated yet...</pre>
            </div>
          </div>
        </div>
      </div>
    `;

        this.attachEventListeners();
    }

    private attachEventListeners(): void {
        const form = document.getElementById('generation-form') as HTMLFormElement;
        form?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.startGeneration();
        });

        const stopBtn = document.getElementById('btn-stop');
        stopBtn?.addEventListener('click', () => {
            this.stopGeneration();
        });
    }

    private async startGeneration(): Promise<void> {
        if (this.isGenerating) return;

        // Reset UI
        this.isGenerating = true;
        this.updateControlsState();
        this.gameId = null;
        this.logs = [];
        this.updateStatusBadge('running');
        this.log('🚀 Starting generation request...');
        this.renderLogs();
        const jsonOutput = document.getElementById('json-output') as HTMLElement;
        if (jsonOutput) jsonOutput.textContent = 'Processing...';

        const getVal = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value;

        // Flattened payload to match GenerationRequestSchema
        const requestPayload = {
            targetLanguage: getVal('gen-language'),
            proficiencyLevel: getVal('gen-proficiency'), // Already lowercase in select
            learningSubject: getVal('gen-topic'), // Backend params
            gameStyle: getVal('gen-style'), // Backend params
            sceneSetting: getVal('gen-setting'),
            difyApiKey: getVal('gen-apikey') || undefined, // Optional override
        };

        try {
            // 1. Trigger Generation
            // POST /v1/game-generation (matches Worker route)
            const response = await this.apiClient.post<any>('/v1/game-generation', requestPayload);

            this.log(`✅ Generation started. Game ID: ${response.data.data.gameId}`);
            this.gameId = response.data.data.gameId;

            // 2. Start Polling
            if (this.gameId) {
                this.startPolling(this.gameId);
            } else {
                throw new Error('No gameId returned');
            }

        } catch (error) {
            this.handleError(error);
        }
    }

    private startPolling(gameId: string): void {
        if (this.pollInterval) clearInterval(this.pollInterval);

        this.pollInterval = setInterval(async () => {
            try {
                // GET /v1/game-generation/status/:gameId
                const response = await this.apiClient.get<any>(`/v1/game-generation/status/${gameId}`);
                const status = response.data.data;

                this.updateStatusBadge(status.status);

                if (status.status === 'completed') {
                    this.log('🎉 Generation completed successfully!');
                    this.log('📥 Fetching game content via proxy...');
                    await this.fetchAndRenderGameContent(status.gameId);
                    this.stopGeneration(true); // Success stop
                } else if (status.status === 'failed') {
                    this.log(`❌ Generation failed: ${status.errorMessage || 'Unknown error'}`);
                    this.stopGeneration(false); // Error stop
                } else {
                    this.log(`Processing... Status: ${status.status} (${status.progress || 0}%)`);
                }

            } catch (error) {
                this.log(`⚠️ Polling error: ${error instanceof Error ? error.message : String(error)}`);
                // Don't stop immediately on poll error, might be transient network issue
            }
        }, 2000); // Poll every 2s
    }

    private stopGeneration(success: boolean = false): void {
        this.isGenerating = false;
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this.updateControlsState();
        if (!success) {
            this.updateStatusBadge('stopped');
        }
    }

    private handleError(error: unknown): void {
        console.error('Generation Error:', error);
        this.log(`💥 Error: ${error instanceof Error ? error.message : String(error)}`);
        this.stopGeneration();
        this.updateStatusBadge('failed');
    }

    private updateControlsState(): void {
        const btnGenerate = document.getElementById('btn-generate') as HTMLButtonElement;
        const btnStop = document.getElementById('btn-stop') as HTMLButtonElement;
        const formInputs = document.querySelectorAll('#generation-form input, #generation-form select, #generation-form textarea');

        if (btnGenerate) {
            btnGenerate.disabled = this.isGenerating;
            btnGenerate.innerHTML = this.isGenerating
                ? '<span class="spinner"></span> Generating...'
                : '<span>✨</span> Generate Game';
        }

        if (btnStop) {
            btnStop.disabled = !this.isGenerating;
        }

        formInputs.forEach(input => {
            (input as HTMLInputElement).disabled = this.isGenerating;
        });
    }

    private updateStatusBadge(status: string): void {
        const badge = document.getElementById('status-badge');
        if (!badge) return;

        badge.className = 'badge';
        switch (status) {
            case 'running':
                badge.classList.add('badge-info', 'pulse');
                badge.textContent = 'Running';
                break;
            case 'succeeded':
                badge.classList.add('badge-success');
                badge.textContent = 'Succeeded';
                break;
            case 'failed':
                badge.classList.add('badge-error');
                badge.textContent = 'Failed';
                break;
            case 'stopped':
                badge.classList.add('badge-warning');
                badge.textContent = 'Stopped';
                break;
            default:
                badge.classList.add('badge-neutral');
                badge.textContent = 'Idle';
        }
    }

    private log(message: string): void {
        const timestamp = new Date().toLocaleTimeString();
        this.logs.unshift(`[${timestamp}] ${message}`);
        this.renderLogs();
    }

    private renderLogs(): void {
        const container = document.getElementById('status-log');
        if (!container) return;

        container.innerHTML = this.logs.map(log => `<div class="log-entry">${log}</div>`).join('');
    }

    private renderResult(data: any): void {
        const container = document.getElementById('json-output');
        if (!container) return;

        try {
            // Format JSON nicely
            const json = JSON.stringify(data, null, 2);
            container.textContent = json;

            // Auto-expand the results area
            container.parentElement?.scrollIntoView({ behavior: 'smooth' });
        } catch (e) {
            container.textContent = 'Error formatting result JSON';
        }
    }

    private async fetchAndRenderGameContent(gameId: string): Promise<void> {
        try {
            const { data } = await this.apiClient.get(`/v1/game-generation/games/${gameId}/content`);
            this.log('✅ Game content loaded successfully');
            this.renderResult(data);
        } catch (error) {
            this.log(`❌ Error loading game content: ${error instanceof Error ? error.message : String(error)}`);
            const container = document.getElementById('json-output');
            if (container) container.textContent = `Error loading content from API proxy.\n\nGame ID: ${gameId}\n\nError: ${error instanceof Error ? error.message : String(error)}`;
        }
    }

    destroy(): void {
        this.stopGeneration();
        this.container.innerHTML = '';
    }
}
